import { type Ref } from 'vue';
import { storeToRefs } from 'pinia';

import { MessageUtilities, useMessageUtilities } from '@/library/message/useMessageUtilities';
import { switcher } from '@/common/switcher';
import type { BaseConnectWizardEvent } from '@/library/support-wizard';
import {
  ConnectRequestType,
  ConnectWizardEvent,
  isConnectToSupportEvent,
  isDescriptionSubmittedEvent,
  isTransactionInputEvent,
  isVisitorInfoSubmittedEvent,
} from '@/library/support-wizard';
import { MessageType, NoticeType } from '@/library/message/enum';
import {
  BotConstants,
  createNotice,
  createSystemMessage,
  createUserSystemMessage,
  createUserTransactionMessage,
} from '@/library/message/lib';
import { useI18n } from '@/plugins/i18n';
import { useBotUser } from '@/stores/useBotUser';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { generateIssueMessage } from '@/common/transaction-utils';
import { TransactionType } from '@/common/config';

/**
 * Add messages to the conversations based on various events happening on the wizard.
 */
export function useSupportWizardMessaging({
  conversation,
  ticketId,
}: {
  conversation?: MessageUtilities;
  ticketId: Ref<number | undefined>;
}) {
  const wizardMessaging = conversation || useMessageUtilities();

  const i18n = useI18n();
  const auth = useBotUser();
  const connectConfigStore = useConnectConfig();
  const { connectConfig } = storeToRefs(connectConfigStore);

  const wizardEventSwitch = switcher()
    .when(
      (evt) => isConnectToSupportEvent(evt) && evt.payload.type === ConnectRequestType.OPEN_REQUEST,
      () => {
        const requestId = ticketId.value;

        // A missing ticket id means the ticket was not created. Skip the confirmation rather than
        // show an empty number.
        if (!requestId) return;

        wizardMessaging.addItems(
          createNotice({
            type: NoticeType.TICKET_CREATED,
            message: i18n.t('page.connect.open_request.created', { requestId }) as string,
          })
        );
      }
    )
    .when(isVisitorInfoSubmittedEvent, ({ payload: { description, visitorInfo } }) => {
      const { displayName, email, phone = '' } = visitorInfo || {};
      let { name = '' } = visitorInfo || {};
      name = name || displayName || '';

      email &&
        wizardMessaging.addItems(
          createUserSystemMessage({
            message: i18n.t('chat.user.info.email', [email]),
            id: BotConstants.IDS.USER_EMAIL,
          })
        );

      phone &&
        wizardMessaging.addItems(
          createUserSystemMessage({
            message: i18n.t('chat.user.info.phone', [phone]),
            id: BotConstants.IDS.USER_PHONE,
          })
        );

      name &&
        wizardMessaging.addItems(
          createUserSystemMessage({
            message: i18n.t('chat.user.info.name', [name]),
            id: BotConstants.IDS.USER_DISPLAY_NAME,
          })
        );

      description &&
        wizardMessaging.addItems(
          createUserSystemMessage({
            message: i18n.t('chat.user.info.issue_description', [description]),
            id: BotConstants.IDS.USER_DESCRIPTION,
            noSystemPrefix: true,
          })
        );

      const suppressConnectPromptAfterVisitorInfo = Boolean(connectConfig.value?.aiConversation);
      if (!suppressConnectPromptAfterVisitorInfo) {
        wizardMessaging.addItems(
          createSystemMessage({
            type: MessageType.DEFAULT,
            message: i18n.t('chat.sys.message.connect'),
            id: BotConstants.IDS.HOW_TO_GET_IN_TOUCH,
          })
        );
      }
    })
    .when(isDescriptionSubmittedEvent, ({ payload: { description } }) => {
      description &&
        wizardMessaging.addItems(
          createUserSystemMessage({
            message: i18n.t('chat.user.info.issue_description', [description]),
            id: BotConstants.IDS.USER_DESCRIPTION,
            noSystemPrefix: true,
          })
        );

      wizardMessaging.addItems(
        createSystemMessage({
          type: MessageType.DEFAULT,
          message: i18n.t('chat.sys.message.connect'),
          id: BotConstants.IDS.HOW_TO_GET_IN_TOUCH,
        })
      );
    })
    .when(isTransactionInputEvent, (evt) => {
      const { transactionType, transactions } = evt.payload;
      const none = transactions.length === 0;
      const issues = transactions.map((tx) =>
        generateIssueMessage({
          type: transactionType,
          transaction: tx,
        })
      );

      const message = none
        ? {
            [TransactionType.Deposit]: i18n.t('chat.user.issue.different.deposit'),
            [TransactionType.Withdrawal]: i18n.t('chat.user.issue.different.withdraw'),
            [TransactionType.Bet]: i18n.t('chat.user.issue.different.bet'),
          }[transactionType] || ''
        : issues.length
          ? null
          : i18n.t('chat.user.issue.no_records');

      if (message) {
        // wizardMessaging.addItems(
        //   createUserTransactionMessage({
        //     issues,
        //     message,
        //     id: BotConstants.IDS.USER_ISSUE_LIST,
        //   })
        // );
      }

      wizardMessaging.addItems(
        createSystemMessage({
          type: MessageType.DEFAULT,
          message: i18n.t('chat.sys.issue.ask_detail'),
          id: BotConstants.IDS.ISSUE_SYSTEM_DESCRIBE_ISSUE,
        })
      );
    })
    .when(
      (evt: BaseConnectWizardEvent) => evt.type === ConnectWizardEvent.loginSuccessful,
      () => {
        wizardMessaging.addItems(
          createSystemMessage({
            type: MessageType.DEFAULT,
            id: BotConstants.IDS.LOGGED_IN_BY,
            message: i18n.t('chat.sys.login.success', { name: auth.user.displayName }),
          })
        );
      }
    );
  // .when(
  //   (evt) => isConnectToSupportEvent(evt) && evt.payload.type === 'chat',
  //   () => {
  //     wizardMessaging.addItems(
  //       createUserSystemMessage({
  //         message: i18n.t('chat.user.response.action', { action: i18n.t('page.connect.live_chat') }),
  //         id: BotConstants.IDS.USER_LIVE_CHAT,
  //         sendToChat: false,
  //       })
  //     );
  //   }
  // );

  return {
    messaging: { ...wizardMessaging },
    reset: () => wizardMessaging.clear({ __CLEARING_CONVERSATION: true }),
    listenWizardEvents: (evt: BaseConnectWizardEvent) => wizardEventSwitch.match(evt),
  };
}
